import {
  LabelValueDetails,
  LabelValueDetailsInput,
  TimeSignalClassification,
  UpdateTimeSignalInput,
  TimeSignal,
  TimeSignalAcceptedBy,
  LabelValueSource,
  LabelValueAcceptedBy,
  LabelSuggestionDetails,
  LabelValue
} from 'graphql/types';
import {
  mapLabelValuesToTimesheetLabels,
  mapTimesheetLabel
} from '../../common';
import { TimesheetLabel } from '../../../timesheet-labels/labelSchema';
import { shouldAcceptSuggestion } from '../../../common/utils/shouldAcceptSuggestion';

interface FeatureFlags {
  isSmartSuggestionEnabled: boolean;
  isTimeSignalFederatedSuggestionsEnabled: boolean;
  isEmptyLabelsEnabled: boolean;
}

interface TimeSignalGroupUpdates {
  labelValues?: TimesheetLabel[];
  classificationUpdate?: TimeSignalClassification;
  acceptedBy?: TimeSignalAcceptedBy;
  excluded?: boolean;
}

interface GetUpdateTimeSignalInputInput {
  timeSignal: TimeSignal;
  updates: TimeSignalGroupUpdates;
  flags: FeatureFlags;
}

interface GetLabelValuesUpdateInput {
  currentLabelValues: LabelValueDetails[];
  currentSuggestions: LabelSuggestionDetails[];
  updates: TimeSignalGroupUpdates;
  flags: FeatureFlags;
}

const combineTimesheetLabels = (a: TimesheetLabel[], b: TimesheetLabel[]) => {
  const aMapTuples: [
    string,
    TimesheetLabel
  ][] = a.map((label: TimesheetLabel) => [label.id, label]);
  const bMapTuples: [
    string,
    TimesheetLabel
  ][] = b.map((label: TimesheetLabel) => [label.id, label]);

  const map = new Map([...aMapTuples, ...bMapTuples]);
  return Array.from(map.values());
};

const acceptSuggestion = (
  labels: TimesheetLabel[],
  suggestions: LabelSuggestionDetails[],
  flags: FeatureFlags
): TimesheetLabel[] => {
  return labels.map((label: TimesheetLabel) => {
    const suggestion = suggestions?.find(
      (suggestion: LabelSuggestionDetails) => suggestion?.label?.id == label?.id
    );

    if (shouldAcceptSuggestion(label, flags.isEmptyLabelsEnabled)) {
      label.value = flags.isTimeSignalFederatedSuggestionsEnabled
        ? (suggestion?.suggestedValues?.[0] as LabelValue)
        : label.suggestedValues?.[0];
    }
    if (
      flags.isTimeSignalFederatedSuggestionsEnabled &&
      label.valueSource === LabelValueSource.Suggestion &&
      !label.acceptedBy
    ) {
      // Backwards compatibility: If federatedSuggestions is enabled there could be a legacy suggestion at label.value that is invisible to the user.
      // however the visible federated suggestion the user sees and meant to accept is at label.suggestedValues[0]
      // So if there is a non-accepted label.value and federatedSuggestions is enabled then we overwrite it.
      label.value = flags.isTimeSignalFederatedSuggestionsEnabled
        ? (suggestion?.suggestedValues?.[0] as LabelValue)
        : label.suggestedValues?.[0];
    }
    label.acceptedBy = LabelValueAcceptedBy.User;
    return label;
  });
};

const getLabelValuesUpdate = ({
  currentLabelValues,
  currentSuggestions,
  updates,
  flags
}: GetLabelValuesUpdateInput): LabelValueDetailsInput[] => {
  const existingLabelValues = mapLabelValuesToTimesheetLabels({
    labelValues: currentLabelValues,
    isSmartSuggestionEnabled: flags.isSmartSuggestionEnabled
  });

  let labelValues = existingLabelValues;
  if (updates?.classificationUpdate === TimeSignalClassification.Accepted) {
    labelValues = acceptSuggestion(
      existingLabelValues,
      currentSuggestions,
      flags
    );
  }
  labelValues = combineTimesheetLabels(labelValues, updates.labelValues ?? []);

  return labelValues.map((lv: TimesheetLabel) =>
    mapTimesheetLabel(flags.isSmartSuggestionEnabled, lv)
  );
};

export const GetUpdateTimeSignalInput = ({
  timeSignal,
  updates,
  flags
}: GetUpdateTimeSignalInputInput): UpdateTimeSignalInput => {
  const labelValuesUpdate = getLabelValuesUpdate({
    currentLabelValues: timeSignal.labelValues,
    currentSuggestions: timeSignal?.suggestions?.suggestions,
    updates,
    flags
  });

  return {
    id: timeSignal.id,
    labelValues: labelValuesUpdate,
    classification: updates?.classificationUpdate ?? timeSignal.classification,
    acceptedBy:
      updates.classificationUpdate === TimeSignalClassification.Accepted ||
      (updates.classificationUpdate === undefined &&
        timeSignal.classification === TimeSignalClassification.Accepted)
        ? TimeSignalAcceptedBy.User
        : undefined,
    excluded: updates.excluded ?? timeSignal.excluded
  };
};
